import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus, Trash2, Check, Sparkles } from 'lucide-react';
import { apiService } from '@/core/api/service';
import { useAppState } from '@/core/app-context';
import { PageHeader } from '@/components/PageHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export const Route = createFileRoute('/tenants')({
  head: () => ({
    meta: [{ title: 'Tenant · Replya Internal Backoffice' }],
  }),
  component: TenantsPage,
});

function TenantsPage() {
  const { selectedTenant, selectTenant, clearTenant } = useAppState();
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<string | null>(null);
  const [newTenant, setNewTenant] = useState({ name: '', dbName: '' });

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => apiService.listTenants(),
  });

  const createMutation = useMutation({
    mutationFn: apiService.createTenant,
    onSuccess: (created) => {
      toast.success(`Tenant "${created.name}" creato`);
      setIsCreateOpen(false);
      setNewTenant({ name: '', dbName: '' });
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: () => {
      toast.error("Errore durante la creazione del tenant");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tenantId: string) => apiService.deleteTenant(tenantId),
    onSuccess: (_, tenantId) => {
      toast.success('Tenant eliminato');
      if (selectedTenant?.tenantId === tenantId) {
        clearTenant();
      }
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: () => {
      toast.error("Errore durante l'eliminazione del tenant");
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTenant.name.trim() || !newTenant.dbName.trim()) return;
    createMutation.mutate(newTenant);
  };

  const handleSelect = (tenant: NonNullable<typeof tenants>[number]) => {
    selectTenant(tenant);
    toast.info(`Tenant selezionato: ${tenant.name}`);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <PageHeader title="Tenant" subtitle="Gestisci i tenant del backoffice.">
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-xl" asChild>
            <Link to="/sector-config">
              <Sparkles className="mr-2 h-4 w-4" />
              Configura AI
            </Link>
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground">
                <Plus className="mr-2 h-4 w-4" />
                Nuovo tenant
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl border bg-surface">
              <DialogHeader>
                <DialogTitle>Crea nuovo tenant</DialogTitle>
                <DialogDescription>
                  Inserisci i dati principali del tenant. Il backend provvederà alla configurazione.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tenant-name">Nome</Label>
                  <Input
                    id="tenant-name"
                    value={newTenant.name}
                    onChange={(e) => setNewTenant((t) => ({ ...t, name: e.target.value }))}
                    placeholder="Es. Coenergia"
                    className="rounded-xl bg-surface-2 border-border"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tenant-db">Nome database</Label>
                  <Input
                    id="tenant-db"
                    value={newTenant.dbName}
                    onChange={(e) => setNewTenant((t) => ({ ...t, dbName: e.target.value }))}
                    placeholder="Es. coenergia_db"
                    className="rounded-xl bg-surface-2 border-border"
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || !newTenant.name.trim() || !newTenant.dbName.trim()}
                    className="rounded-xl bg-brand hover:bg-brand-glow text-brand-foreground"
                  >
                    {createMutation.isPending ? 'Creazione...' : 'Crea tenant'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </PageHeader>

      <Card className="rounded-2xl border bg-surface">
        <CardHeader>
          <CardTitle>Lista tenant</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Caricamento...</p>
          ) : (
            <div className="overflow-x-auto -mx-2 px-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Database</TableHead>
                    <TableHead>Creato il</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants?.map((tenant) => (
                    <TableRow
                      key={tenant.tenantId}
                      className="cursor-pointer"
                      onClick={() => handleSelect(tenant)}
                    >
                      <TableCell>
                        {selectedTenant?.tenantId === tenant.tenantId && (
                          <Check className="h-4 w-4 text-brand" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{tenant.name}</TableCell>
                      <TableCell>{tenant.dbName}</TableCell>
                      <TableCell>{new Date(tenant.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="rounded-xl"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTenantToDelete(tenant.tenantId);
                          }}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!tenants?.length && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        Nessun tenant trovato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={tenantToDelete !== null} onOpenChange={(open: boolean) => !open && setTenantToDelete(null)}>
        <AlertDialogContent className="rounded-2xl border bg-surface">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il tenant?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. Tutti i dati associati al tenant saranno rimossi.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" onClick={() => setTenantToDelete(null)}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (tenantToDelete) {
                  deleteMutation.mutate(tenantToDelete);
                  setTenantToDelete(null);
                }
              }}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
